// Low-level source scanner that walks character-by-character, tracking
// string/comment state, recognizing dialect-aware batch separators and
// delimiter flips, and emitting statement boundaries. Pure function — no
// tree-sitter dependency. PRD § 2.6 + § G12.

import type { ParseOptions } from '../model';
import {
  blockTrackingEngine,
  feedBlockWord,
  newBlockState,
  resetBlockState,
  shouldSuppressSplit,
} from './compoundBlocks';

export interface ScanRange {
  startOffset: number;
  endOffset: number;
}

export interface ScanStatement extends ScanRange {
  text: string;
  batchIndex: number;
}

export interface ScanBatchSeparator extends ScanRange {
  kind: 'GO' | '/';
  goCount: number;
  batchIndex: number; // index of the batch this separator closes
}

export interface ScanDelimiterFlip extends ScanRange {
  delimiter: string;
}

export interface ScanResult {
  statements: ScanStatement[];
  batchSeparators: ScanBatchSeparator[];
  delimiterFlips: ScanDelimiterFlip[];
  maxBatchIndex: number; // highest batchIndex referenced by any statement/separator; -1 if none
  /** True when the scan ended inside an unterminated string/comment/bracket/
   *  dollar-quote. Line comments end at EOF, so they do NOT count — parity with
   *  the execution splitter's ambiguity fallback (splitForExecution Decision 4). */
  unterminatedAtEof: boolean;
}

const GO_LINE = /^\s*GO(?:\s+(\d+))?\s*$/i;
const SLASH_LINE = /^\s*\/\s*$/;
const DELIMITER_LINE = /^\s*DELIMITER\s+(\S+)\s*$/i;
const WS = /[\s\r\n]/;

function findLineEnd(source: string, from: number): number {
  let j = from;
  while (j < source.length && source.charCodeAt(j) !== 10) j++;
  return j;
}

// Word characters for keyword scanning (A-Z a-z 0-9 _).
function isWordChar(code: number): boolean {
  return (
    (code >= 65 && code <= 90) ||
    (code >= 97 && code <= 122) ||
    (code >= 48 && code <= 57) ||
    code === 95
  );
}
function isWordStartChar(code: number): boolean {
  return (code >= 65 && code <= 90) || (code >= 97 && code <= 122) || code === 95;
}

// Oracle alternative-quoting literal `q'<d>…<d>'` (optionally `n`/`N` prefixed).
// When `source[i]` opens one, returns the closing sequence (`<d>'`, e.g. `]'`)
// and the index of the first content char; else null. The `q`/`nq` must start a
// token (no leading word char) so a column named `q` can't be misread. Mirrors
// treeSitterSyntaxLex.scanOracleQQuote but drives the scanner's state machine so
// the same neutralization applies to statement splitting (F3 — a `;`/`'` inside
// the literal must not create a boundary).
function scanOracleQQuoteOpen(
  source: string,
  i: number,
): { close: string; contentStart: number } | null {
  if (i > 0 && isWordChar(source.charCodeAt(i - 1))) return null;
  const c = source[i];
  const isQ = c === 'q' || c === 'Q';
  const isNQ = (c === 'n' || c === 'N') && (source[i + 1] === 'q' || source[i + 1] === 'Q');
  if (!isQ && !isNQ) return null;
  const qIdx = isNQ ? i + 1 : i;
  if (source[qIdx + 1] !== "'") return null;
  const open = source[qIdx + 2];
  if (open === undefined) return null;
  const close =
    open === '[' ? ']' : open === '{' ? '}' : open === '(' ? ')' : open === '<' ? '>' : open;
  return { close: `${close}'`, contentStart: qIdx + 3 };
}

// MySQL/MariaDB executable comment opener at `i` (`source[i..i+1]` is `/*`):
// `/*!`, `/*!NNNNN`, `/*M!`, `/*M!NNNNN`. The server EXECUTES the payload of
// these, so a range holding nothing but one of them is a real statement —
// mysqldump emits every trigger and its session-setup `SET`s this way. Mirrors
// `codeProjection.ts`'s `execCommentOpenerLen` (WS-E fix2 F1a).
function isExecutableCommentOpener(source: string, i: number): boolean {
  const c3 = source[i + 2];
  return c3 === '!' || (c3 === 'M' && source[i + 3] === '!');
}

// True when the [start, end) range contains at least one non-whitespace,
// non-comment character. Used to skip "comment-only" ranges that would
// otherwise get emitted as statements. With `executableComments` (mysql) a
// `/*! … */` block counts as code — dropping it would silently discard
// executable SQL from a dump (release review 1.5.0, F1).
function hasCodeBetween(
  source: string,
  start: number,
  end: number,
  supportsHashComment: boolean,
  executableComments: boolean,
): boolean {
  let i = start;
  while (i < end) {
    const c = source[i];
    const c2 = source[i + 1] ?? '';
    if (WS.test(c)) { i++; continue; }
    if (c === '-' && c2 === '-') {
      while (i < end && source[i] !== '\n') i++;
      continue;
    }
    if (c === '/' && c2 === '*') {
      if (executableComments && isExecutableCommentOpener(source, i)) return true;
      i += 2;
      while (i < end && !(source[i] === '*' && source[i + 1] === '/')) i++;
      if (i < end) i += 2;
      continue;
    }
    if (supportsHashComment && c === '#') {
      while (i < end && source[i] !== '\n') i++;
      continue;
    }
    return true;
  }
  return false;
}

// eslint-disable-next-line max-lines-per-function -- single-pass scanner; splitting further hurts readability
export function scanDocument(source: string, options: ParseOptions): ScanResult {
  const engine = options.engine;
  const supportsGo = engine === 'mssql';
  const supportsSlash = engine === 'oracle';
  const supportsDelimiter = engine === 'mysql';
  const supportsBracket = engine === 'mssql';
  const supportsBacktick = engine === 'mysql';
  const supportsDollar = engine === 'postgres';
  const supportsQQuote = engine === 'oracle';
  const supportsHashComment = engine === 'mysql';
  const executableComments = engine === 'mysql';
  // PostgreSQL is the only engine whose block comments NEST (an inner `/* … */`
  // does not close the outer comment) — mysql/mssql/sqlite/oracle all treat the
  // first `*/` as the close. Engine-gated exactly like supportsDollar/backtick
  // above so single-level stays engine-correct everywhere else.
  const nestedBlockComments = engine === 'postgres';

  const statements: ScanStatement[] = [];
  const batchSeparators: ScanBatchSeparator[] = [];
  const delimiterFlips: ScanDelimiterFlip[] = [];

  // Compound BEGIN…END block awareness — only for routine/trigger bodies on the
  // tracked engines (null for postgres, which is dollar-quote protected). See
  // compoundBlocks.ts for the safety gates.
  const trackedEngine = blockTrackingEngine(options);
  const treatBareBeginAsRoutineBody = options.treatBareBeginAsRoutineBody === true;
  const blockState = newBlockState();

  let currentDelimiter = options.currentDelimiter ?? ';';
  type State = 'code' | 'line-comment' | 'block-comment' | 'single' | 'double' | 'backtick' | 'bracket' | 'dollar' | 'qquote';
  let state: State = 'code';
  let dollarTag = '';
  // Closing sequence for the currently open Oracle q-quote (`<d>'`, e.g. `]'`).
  let qClose = '';
  // Nesting depth while inside a block comment. Always 1 for single-level
  // engines (first `*/` returns to 0 → code); pg increments on each inner `/*`.
  let blockDepth = 0;
  let batchIndex = 0;
  let stmtStart = 0;
  let maxBatchIndex = -1;
  let i = 0;
  const len = source.length;

  function emitStatement(endExclusive: number): void {
    let s = stmtStart;
    while (s < endExclusive && WS.test(source[s])) s++;
    let e = endExclusive;
    while (e > s && WS.test(source[e - 1])) e--;
    if (s < e && hasCodeBetween(source, s, e, supportsHashComment, executableComments)) {
      statements.push({
        startOffset: s,
        endOffset: e,
        text: source.slice(s, e),
        batchIndex,
      });
      if (batchIndex > maxBatchIndex) maxBatchIndex = batchIndex;
    }
    stmtStart = endExclusive;
    resetBlockState(blockState);
  }

  function closeBatchWithSeparator(sep: ScanBatchSeparator): void {
    batchSeparators.push(sep);
    if (sep.batchIndex > maxBatchIndex) maxBatchIndex = sep.batchIndex;
    batchIndex++;
  }

  while (i <= len) {
    // Line-leading client-directive check when in code state.
    if (state === 'code' && (i === 0 || source.charCodeAt(i - 1) === 10)) {
      const lineEnd = findLineEnd(source, i);
      const line = source.slice(i, lineEnd);
      const advancePastLine = (): void => {
        const after = lineEnd + (lineEnd < len ? 1 : 0);
        i = after;
        stmtStart = after;
      };
      let handled = false;
      if (supportsGo) {
        const m = line.match(GO_LINE);
        if (m) {
          emitStatement(i);
          closeBatchWithSeparator({
            startOffset: i,
            endOffset: lineEnd,
            kind: 'GO',
            goCount: m[1] ? Number(m[1]) : 1,
            batchIndex,
          });
          advancePastLine();
          handled = true;
        }
      }
      if (!handled && supportsSlash) {
        const m = line.match(SLASH_LINE);
        if (m) {
          emitStatement(i);
          closeBatchWithSeparator({
            startOffset: i,
            endOffset: lineEnd,
            kind: '/',
            goCount: 1,
            batchIndex,
          });
          advancePastLine();
          handled = true;
        }
      }
      if (!handled && supportsDelimiter) {
        const m = line.match(DELIMITER_LINE);
        if (m) {
          emitStatement(i);
          delimiterFlips.push({ startOffset: i, endOffset: lineEnd, delimiter: m[1] });
          currentDelimiter = m[1];
          advancePastLine();
          handled = true;
        }
      }
      if (handled) continue;
    }

    if (i >= len) break;
    const c = source[i];
    const c2 = source[i + 1] ?? '';

    if (state === 'code') {
      // Oracle q-quote — checked BEFORE the keyword scan so `q'…'` is not
      // consumed as a bare `q` word (which would then enter `single` on the
      // following `'` and mis-split on the literal's inner `;`/`'`).
      if (supportsQQuote) {
        const q = scanOracleQQuoteOpen(source, i);
        if (q) { state = 'qquote'; qClose = q.close; i = q.contentStart; continue; }
      }
      // Keyword scan for compound BEGIN…END tracking. A word-start char is
      // never a comment/string/delimiter start, so consuming the whole word
      // here cannot skip a special token. Only runs for tracked engines.
      if (trackedEngine) {
        const code = source.charCodeAt(i);
        if (isWordStartChar(code) && (i === 0 || !isWordChar(source.charCodeAt(i - 1)))) {
          let j = i + 1;
          while (j < len && isWordChar(source.charCodeAt(j))) j++;
          feedBlockWord(blockState, source.slice(i, j), trackedEngine, treatBareBeginAsRoutineBody);
          i = j;
          continue;
        }
      }
      if (c === '-' && c2 === '-') { state = 'line-comment'; i += 2; continue; }
      if (supportsHashComment && c === '#') { state = 'line-comment'; i++; continue; }
      if (c === '/' && c2 === '*') { state = 'block-comment'; blockDepth = 1; i += 2; continue; }
      if (c === "'") { state = 'single'; i++; continue; }
      // Enter `double` state on `"` regardless of engine — intentionally
      // dialect-agnostic. For statement-boundary splitting it does not matter
      // whether `"…"` is a quoted IDENTIFIER (Postgres/Oracle/standard SQL,
      // MySQL with ANSI_QUOTES) or a STRING literal (MySQL without ANSI_QUOTES):
      // an embedded `;`/delimiter inside either reading must not split, and `""`
      // is the doubling-escape in both cases. Adding an engine gate here would
      // be pointless complexity. Known shared limitation: like `single` and
      // `backtick`, `double` does not model backslash escapes — acceptable
      // because a stray backslash cannot produce a wrong statement split.
      if (c === '"') { state = 'double'; i++; continue; }
      if (supportsBacktick && c === '`') { state = 'backtick'; i++; continue; }
      if (supportsBracket && c === '[') { state = 'bracket'; i++; continue; }
      if (supportsDollar && c === '$') {
        const tagMatch = source.slice(i).match(/^\$([A-Za-z_][A-Za-z0-9_]*)?\$/);
        if (tagMatch) { state = 'dollar'; dollarTag = tagMatch[0]; i += dollarTag.length; continue; }
      }
      if (currentDelimiter.length > 0 && source.startsWith(currentDelimiter, i)) {
        // Inside an open routine BEGIN…END block the delimiter is body text,
        // not a statement boundary — consume it without splitting.
        if (trackedEngine && shouldSuppressSplit(blockState)) {
          i += currentDelimiter.length;
          continue;
        }
        emitStatement(i);
        i += currentDelimiter.length;
        stmtStart = i;
        continue;
      }
      i++;
    } else if (state === 'line-comment') {
      if (c === '\n') state = 'code';
      i++;
    } else if (state === 'block-comment') {
      // pg nests: an inner `/*` deepens the comment; the close only returns to
      // code once every open `/*` is matched. Single-level engines never take
      // the increment branch, so the first `*/` (blockDepth 1→0) closes them.
      if (nestedBlockComments && c === '/' && c2 === '*') {
        blockDepth++;
        i += 2;
      } else if (c === '*' && c2 === '/') {
        blockDepth--;
        i += 2;
        if (blockDepth === 0) state = 'code';
      } else i++;
    } else if (state === 'single') {
      if (c === "'") {
        if (c2 === "'") { i += 2; } else { state = 'code'; i++; }
      } else i++;
    } else if (state === 'double') {
      if (c === '"') {
        if (c2 === '"') { i += 2; } else { state = 'code'; i++; }
      } else i++;
    } else if (state === 'backtick') {
      if (c === '`') {
        if (c2 === '`') { i += 2; } else { state = 'code'; i++; }
      } else i++;
    } else if (state === 'bracket') {
      if (c === ']') {
        if (c2 === ']') { i += 2; } else { state = 'code'; i++; }
      } else i++;
    } else if (state === 'dollar') {
      if (source.startsWith(dollarTag, i)) { state = 'code'; i += dollarTag.length; } else i++;
    } else if (state === 'qquote') {
      if (source.startsWith(qClose, i)) { state = 'code'; i += qClose.length; } else i++;
    }
  }

  // Trailing statement (unterminated last statement).
  emitStatement(len);

  // Terminal-state check: `line-comment` ends at EOF by definition and `code`
  // is clean; every other state means an unterminated construct.
  const unterminatedAtEof = state !== 'code' && state !== 'line-comment';

  return { statements, batchSeparators, delimiterFlips, maxBatchIndex, unterminatedAtEof };
}
